import { getToken } from './auth'
import type { Company, UserNote, UserAnswer, Recording, QuestionCategory, Question, User, NavTrack, InviteCode } from './types'

const BASE = '/api'

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(opts?.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (opts?.body && typeof opts.body === 'string') headers['Content-Type'] = 'application/json'

  const res = await fetch(`${BASE}${path}`, { ...opts, headers })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${text}`)
  }
  return res.json()
}

export const api = {
  // Auth
  async login(email: string): Promise<{ ok: boolean; message: string }> {
    return request('/auth/login', { method: 'POST', body: JSON.stringify({ email }) })
  },

  async verify(code: string, inviteCode?: string): Promise<{ token: string; user: User }> {
    return request('/auth/verify', { method: 'POST', body: JSON.stringify({ code, inviteCode }) })
  },

  async me(): Promise<User> {
    return request('/auth/me')
  },

  async updateProfile(name: string): Promise<User> {
    return request('/auth/me', { method: 'PUT', body: JSON.stringify({ name }) })
  },

  // Companies
  async listCompanies(params?: { category?: string; search?: string }): Promise<Company[]> {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.search) qs.set('search', params.search)
    const q = qs.toString()
    return request(`/companies${q ? '?' + q : ''}`)
  },

  async getCompany(id: string): Promise<Company> {
    return request(`/companies/${id}`)
  },

  // Question categories & questions
  async listQuestionCategories(): Promise<QuestionCategory[]> {
    return request('/questions/categories')
  },

  async listQuestions(categoryId?: string): Promise<Question[]> {
    const q = categoryId ? `?category=${categoryId}` : ''
    return request(`/questions${q}`)
  },

  // Notes
  async getNote(companyId: string): Promise<UserNote | null> {
    try {
      return await request(`/notes/${companyId}`)
    } catch { return null }
  },

  async saveNote(companyId: string, content: string): Promise<UserNote> {
    return request('/notes', { method: 'POST', body: JSON.stringify({ company_id: companyId, content }) })
  },

  // Answers
  async getAnswers(companyId: string): Promise<UserAnswer[]> {
    return request(`/answers?company_id=${companyId}`)
  },

  async saveAnswer(companyId: string, questionId: string, answer: string, rating?: number): Promise<UserAnswer> {
    return request('/answers', {
      method: 'POST',
      body: JSON.stringify({ company_id: companyId, question_id: questionId, answer, rating }),
    })
  },

  // Recordings
  async listRecordings(companyId: string): Promise<Recording[]> {
    return request(`/recordings?company_id=${companyId}`)
  },

  async uploadRecording(companyId: string, blob: Blob, filename: string): Promise<Recording> {
    const formData = new FormData()
    formData.append('file', blob, filename)
    formData.append('company_id', companyId)
    const token = getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${BASE}/recordings/upload`, { method: 'POST', body: formData, headers })
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
    return res.json()
  },

  async getRecordingUrl(id: string): Promise<string> {
    const data = await request<{ url: string }>(`/recordings/${id}`)
    return data.url
  },

  // Navigation tracks
  async listTracks(): Promise<NavTrack[]> {
    return request('/tracks')
  },

  async getTrackProgress(): Promise<{ track_id: string; current_stop: number }[]> {
    return request('/tracks/progress')
  },

  async updateTrackProgress(trackId: string, currentStop: number): Promise<void> {
    await request('/tracks/progress', { method: 'POST', body: JSON.stringify({ trackId, currentStop }) })
  },

  // AI
  async aiChat(messages: { role: string; content: string }[], context?: string): Promise<{ reply: string }> {
    return request('/ai/chat', { method: 'POST', body: JSON.stringify({ messages, context }) })
  },

  async aiAnalyze(text: string, companyName?: string, type: 'summary' | 'social' | 'both' = 'both'): Promise<{ analysis: string }> {
    return request('/ai/analyze', { method: 'POST', body: JSON.stringify({ text, companyName, type }) })
  },

  // Admin
  async adminListUsers(): Promise<(User & { approved: boolean; invite_code_used?: string; created_at: string; last_login?: string })[]> {
    return request('/admin/users')
  },

  async adminApproveUser(userId: string, approved: boolean): Promise<User> {
    return request('/admin/users', { method: 'PUT', body: JSON.stringify({ userId, approved }) })
  },

  async adminDeleteUser(userId: string): Promise<void> {
    await request(`/admin/users?userId=${userId}`, { method: 'DELETE' })
  },

  async adminListInviteCodes(): Promise<InviteCode[]> {
    return request('/admin/invite-codes')
  },

  async adminAudit(): Promise<{ id: string; actor_email?: string; method: string; path: string; status: number; ip?: string; country?: string; created_at: string }[]> {
    return request('/admin/audit?limit=100')
  },

  // Privacy (GDPR, SPEC-DATA)
  privacyExportUrl(): string {
    return `/api/privacy/export?t=${encodeURIComponent(getToken() || '')}`
  },

  async privacyErase(confirm: string): Promise<{ erased: boolean }> {
    return request('/privacy/erase', { method: 'POST', body: JSON.stringify({ confirm }) })
  },

  async adminCreateInviteCode(opts: { code?: string; label?: string; maxUses?: number; expiresAt?: string }): Promise<InviteCode> {
    return request('/admin/invite-codes', { method: 'POST', body: JSON.stringify(opts) })
  },

  async adminDeactivateCode(code: string): Promise<void> {
    await request(`/admin/invite-codes?code=${code}`, { method: 'DELETE' })
  },

  // Company Research/Analysis
  async getCompanyAnalysis(companyId: string): Promise<any | null> {
    try {
      return await request(`/ai/research?companyId=${companyId}`)
    } catch { return null }
  },

  async runCompanyResearch(companyId: string): Promise<any> {
    return request('/ai/research', { method: 'POST', body: JSON.stringify({ companyId }) })
  },

  async listAllAnalyses(): Promise<any[]> {
    return request('/ai/research')
  },

  async runBatchResearch(companyIds?: string[], limit?: number): Promise<any> {
    return request('/ai/research-batch', { method: 'POST', body: JSON.stringify({ companyIds, limit }) })
  },
}
